import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { PlayCircle, Film } from 'lucide-react';
import { trackTour } from '../lib/analytics';

// ────────────────────────────────────────────────────────────────
// Paste your YouTube video ID here once the tour video is uploaded.
// Example: for https://www.youtube.com/watch?v=AbCdEf12345  →  'AbCdEf12345'
// Leave as '' to show a friendly "coming soon" placeholder.
export const TOUR_VIDEO_ID = '';
// ────────────────────────────────────────────────────────────────

// Controlled dialog — used both by the button and by the Today autoplay flow.
export const TourVideoDialog = ({ open, onOpenChange, autoplay = false, dontShow, onToggleDontShow, onWatched }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl p-0 overflow-hidden gap-0" data-testid="tour-video-dialog">
      <DialogHeader className="px-5 pt-5 pb-3">
        <DialogTitle className="font-display flex items-center gap-2">
          <Film className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} />
          Larkwell in 2 minutes
        </DialogTitle>
      </DialogHeader>

      {TOUR_VIDEO_ID ? (
        <div className="relative w-full" style={{ aspectRatio: '16 / 9' }}>
          <iframe
            className="absolute inset-0 h-full w-full"
            src={`https://www.youtube-nocookie.com/embed/${TOUR_VIDEO_ID}?rel=0&modestbranding=1`}
            title="Larkwell product tour"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            data-testid="tour-video-iframe"
          />
        </div>
      ) : (
        <div
          className="mx-5 rounded-2xl border border-dashed flex flex-col items-center justify-center text-center px-6 py-12"
          style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--secondary) / 0.4)', aspectRatio: '16 / 9' }}
          data-testid="tour-video-placeholder"
        >
          <div className="h-12 w-12 rounded-2xl grid place-items-center mb-3" style={{ background: 'hsl(var(--primary) / 0.12)' }}>
            <Film className="h-6 w-6" style={{ color: 'hsl(var(--primary))' }} />
          </div>
          <p className="font-600">The video tour is coming soon</p>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-sm">
            In the meantime, try the interactive guided tour, or just tap “Plan my day” to see Larkwell in action.
          </p>
        </div>
      )}

      {autoplay && (
        <div className="flex items-center justify-between gap-3 px-5 py-4">
          <label className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] cursor-pointer select-none">
            <Checkbox checked={dontShow} onCheckedChange={onToggleDontShow} data-testid="tour-dont-show-checkbox" />
            Don't show this again
          </label>
          <Button className="rounded-xl" style={{ background: 'hsl(var(--primary))' }} onClick={() => { onWatched && onWatched(); onOpenChange(false); }} data-testid="tour-autoplay-close-button">
            Got it
          </Button>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

// Simple trigger button (used on Help + empty Today state).
export const TourVideoButton = ({ label = 'Watch the 2-min tour', variant = 'outline', className = '', testId = 'watch-tour-button' }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant={variant}
        className={`rounded-xl gap-2 ${className}`}
        onClick={() => { setOpen(true); trackTour('video', 'opened', 'manual'); }}
        data-testid={testId}
      >
        <PlayCircle className="h-4 w-4" /> {label}
      </Button>
      <TourVideoDialog open={open} onOpenChange={setOpen} />
    </>
  );
};
